import { Injectable, DOCUMENT, inject } from '@angular/core';

import { ApCookieService } from '@ap/persistence';

import { AuthenticationStatus, PageviewAnalyticsData, Product } from '../analytics.model';

import { FALLBACK_PAGENAME } from './pageview-data.tokens';

/**
 * @ngdoc service
 * @name ApPageviewDataService
 * @module ApAnalyticsModule
 **/
@Injectable({ providedIn: 'root' })
export class ApPageviewDataService {
    private readonly document = inject(DOCUMENT);
    private readonly fallbackPagename = inject(FALLBACK_PAGENAME);
    private readonly cookieService = inject(ApCookieService);

    public getPageviewData(
        pageType: string,
        productName: string,
        productCategory: string,
        pageSiteSegment: string,
        pageSiteSection: string,
        platformName: string,
        analyticsEvents?: string[],
        caseId?: string,
        authenticationStatus: AuthenticationStatus = AuthenticationStatus.AUTHENTICATED,
        pageName?: string
    ): PageviewAnalyticsData {
        return new PageviewAnalyticsData(
            this.getPageDomain(),
            pageSiteSegment,
            pageName || this.getPageName(),
            this.getUserConsentStatus(),
            authenticationStatus,
            this.getPlatformEnvironment(),
            platformName,
            pageType,
            pageSiteSection,
            this.getProducts(productName, productCategory),
            analyticsEvents,
            caseId
        );
    }

    public getPathArray(): string[] {
        const pathname: string = this.document.location.pathname;
        const hash: string = this.document.location.hash.replace('#/', '');
        // Only when there is a hash present and the pathname didn't include a '/' at the end, add it
        // Otherwise all pages will end up ending with :Overzicht
        const path: string = hash.length > 0 && !pathname.endsWith('/') ? `${pathname}/${hash}` : pathname + hash;

        const pathMinusFirstSlash: string = path.replace(/^[/]/, '');
        const pathMinusDashes = pathMinusFirstSlash.replace(/-/g, ' ');
        const pathWithoutExtension = pathMinusDashes.replace(/\.html/, '');
        const pathWithoutQueryParams: string = pathWithoutExtension.split('?')[0];
        const pathArray: string[] = pathWithoutQueryParams.split('/');
        return pathArray.map((pathItem) => {
            if (pathItem === '') {
                return this.fallbackPagename;
            }
            return pathItem && pathItem[0].toUpperCase() + pathItem.substring(1);
        });
    }

    private getPageDomain(): string {
        return this.document.location.hostname;
    }

    private getPageName(): string {
        return this.getPathArray().join(':');
    }

    private getUserConsentStatus(): string {
        const CONSENT = 'CLC_GDPR';
        const CONSENT_OLD = 'clc';
        const cookie: string = this.cookieService.get(CONSENT) || this.cookieService.get(CONSENT_OLD);
        return cookie ? decodeURIComponent(cookie).split(':')[0] : '';
    }

    private getPlatformEnvironment(): string {
        try {
            return JSON.parse('{<BOOL_IS_OTA>}') ? 'test' : 'production';
        } catch (e) {
            return 'test';
        }
    }

    private getProducts(productName: string, productCategory: string): Product[] {
        return [new Product(productName, productCategory)];
    }
}
